import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const AdminEmailSequencesPage = () => {
  const { user } = useAuth();
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiCall = async (url, options = {}) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  useEffect(() => {
    loadSequences();
  }, []);

  const loadSequences = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/v1/admin/email-sequences/sequences');
      setSequences(data.sequences);
    } catch (err) {
      setError(`Failed to load sequences: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Email Sequences</h1>
              <p className="text-sm text-muted-foreground mt-1">Manage automated email campaigns</p>
            </div>
            <Link to="/admin" className="btn btn-secondary">
              ← Back to Admin
            </Link>
          </div>
        </div>

        <div className="card-body">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex">
                <div className="text-red-600 text-sm">{error}</div>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-red-400 hover:text-red-600"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Loading sequences...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-foreground">All Sequences</h3>
                <button className="btn btn-primary" disabled>
                  Create New Sequence (Coming Soon)
                </button>
              </div>

              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-background">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-gray-200">
                    {sequences.map((sequence) => (
                      <tr key={sequence.id} className="hover:bg-background">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                          {sequence.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-muted-foreground">
                          {sequence.description || 'No description'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            sequence.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {sequence.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {new Date(sequence.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <Link
                            to={`/admin/email-sequences/${sequence.id}/templates`}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Templates
                          </Link>
                          <span className="text-gray-400">
                            Edit (Coming Soon)
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {sequences.length === 0 && !loading && (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-4xl mb-4">📧</div>
                  <h3 className="text-lg font-medium text-foreground mb-2">No Email Sequences</h3>
                  <p className="text-muted-foreground mb-4">Get started by creating your first email sequence.</p>
                  <button className="btn btn-primary" disabled>
                    Create New Sequence (Coming Soon)  
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEmailSequencesPage;